import React, { useState } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';
import type { Product } from '../../types/order';

interface WeightModalProps {
  product: Product;
  /**
   * Τιμή κιλού/λίτρου στο κανάλι της οθόνης (productDisplayPrice: τιμή του προεπιλεγμένου μεγέθους).
   * null = «Βάρος + τιμή κιλού» (utils/weightedOpenPrice): η τιμή κιλού μπαίνει στο ΕΠΟΜΕΝΟ βήμα,
   * οπότε το παράθυρο δεν δείχνει τιμή καταλόγου ούτε σύνολο.
   */
  pricePerUnit: number | null;
  onConfirm: (product: Product, weight: number) => void;
  onClose: () => void;
}

const WeightModal: React.FC<WeightModalProps> = ({ product, pricePerUnit, onConfirm, onClose }) => {
  const { t } = useTranslation(['products', 'common']);
  const [weightInput, setWeightInput] = useState<string>('');

  const unitPrice = pricePerUnit;
  const unitLabel = product.selling_unit === 'LT' ? 'lt' : 'kg';

  const handleKeyPress = (key: string) => {
    if (key === '←') {
      setWeightInput(prev => prev.slice(0, -1));
    } else if (key === '.') {
      if (!weightInput.includes('.')) {
        setWeightInput(prev => prev + '.');
      }
    } else {
      // Limit to 3 decimal places
      const parts = weightInput.split('.');
      if (parts[1] && parts[1].length >= 3) return;
      setWeightInput(prev => prev + key);
    }
  };

  const handleConfirm = () => {
    const weight = parseFloat(weightInput);
    if (weight > 0) {
      onConfirm(product, weight);
    }
  };

  const isValid = weightInput !== '' && parseFloat(weightInput) > 0;

  return (
    <div 
      className="cashier-overlay fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        data-testid="weight-modal"
        className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="cashier-dialog-header p-4 border-b border-line-strong bg-surface-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-t1">{product.name}</h2>
              <p className="text-sm text-t3">
                {unitPrice === null
                  ? t('weightThenPriceHint', { unit: unitLabel })
                  : t('pricePerUnit', { price: unitPrice.toFixed(2), unit: unitLabel })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-surface-3 hover:bg-surface-4 flex items-center justify-center"
            >
              <X className="h-5 w-5 text-t2" />
            </button>
          </div>
        </div>

        {/* Weight Display */}
        <div className="p-6">
          <div className="bg-surface-2 rounded-2xl p-4 mb-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-t1 mb-1">
                {weightInput || '0'} <span className="text-2xl text-t3">{unitLabel}</span>
              </div>
              {unitPrice !== null && (
                <div className="text-lg text-blue-600 dark:text-blue-400 font-semibold">
                  = {formatMoney((Number(weightInput || 0) * unitPrice))}
                </div>
              )}
            </div>
          </div>

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '←'].map((key) => (
              <button
                key={key}
                onClick={() => handleKeyPress(key)}
                className={`h-14 rounded-xl font-bold text-xl transition-all active:scale-95 ${
                  key === '←' 
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60' 
                    : 'bg-surface-3 text-t1 hover:bg-surface-4'
                }`}
              >
                {key}
              </button>
            ))}
          </div>

          {/* Quick Weight Buttons */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[0.1, 0.25, 0.5, 1].map((weight) => (
              <button
                key={weight}
                onClick={() => setWeightInput(weight.toString())}
                className="py-3 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 font-bold text-sm hover:bg-amber-200 dark:hover:bg-amber-900/60 active:scale-95 transition-all"
              >
                {weight}{unitLabel}
              </button>
            ))}
          </div>

          {/* Add Button */}
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className="w-full h-14 rounded-xl bg-green-600 hover:bg-green-700 disabled:bg-surface-3 disabled:text-t4 disabled:cursor-not-allowed text-white font-bold text-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            <Plus className="h-5 w-5" />
            {t('common:add')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeightModal;
